import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TextInput } from '../../Shared/Component/forms/TextInput';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Button } from '../../Shared/Component/forms/Button';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { DropdownSingle } from '../../Shared/Component/forms/DropdownSingle';
import { DropdownMulti } from '../../Shared/Component/forms/DropdownMulti';
import { TextArea } from '../../Shared/Component/forms/TextArea';
import GenericListManager from '../../Shared/Component/forms/GenericListManager';
import { createJobTemplate } from '../Services/JobPostTemplateService';
import { initialJobTemplate, JobTemplate } from '../Types/JobPostTemplateType';
import { getAllOwnerShipLevel, getAllRoleLevel } from '../../Admin/Services/AdminService';
import { Option } from '../../Admin/Types/AdminType';
import { mapToOptions } from '../../Shared/Component/forms/dropdownHelpers';
import { RichTextArea } from '../../Shared/Component/forms/RichTextArea';

export default function JobPostComponent() {
  const { values, handleInputChange ,
    setAllValues,
    handleValueChange,
    handleRequiredValueChangeForDD,
    handleRequiredValueChange
  } = useFormController<JobTemplate>(initialJobTemplate);
   const [candidate, setCandidate] = useState<JobTemplate | null>(initialJobTemplate);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [alert, setAlert] = useState<AlertProps | null>(null);


  const [roleLevel, setRoleLevel] = useState<Option<string>[]>([]);
  useEffect(() => {
    getAllRoleLevel().then(response => {
      if (response?.data) {
        const formatted = mapToOptions(response.data, "roleLevel", "roleLevel");
        setRoleLevel(formatted); 
      } 
    });
  }, []);




  const [ownerShipLevel, setOwnerShipLevel] = useState<Option<string>[]>([]);
  useEffect(() => {
    getAllOwnerShipLevel().then(response => {
      if (response?.data) {
        const formatted = mapToOptions(response.data, "ownerShipLevel", "ownerShipLevel");
        setOwnerShipLevel(formatted); 
      }
    });
  }, []);


  const workTypeOptions = [
    { label: "Hybrid", value: "HYBRID" },
    { label: "Onsite", value: "ONSITE" },
    { label: "Offshore", value: "OFFSHORE" },
    { label: "Work From Home", value: "WFH" },
  ];

      

  const handleSubmit = async () => {
    setAlert(null);
    
    const recruiterId = localStorage.getItem("userId");
    const payload = { ...values, recruiterId , createdById:recruiterId, companyId:recruiterId};

    try {
      const response = await createJobTemplate(payload);
      if (response.success) {
        setAlert({ variant: "success", title: "Success", message: response.message });
      } else {
        setAlert({ variant: "error", title: "Registration Error", message: response.message });
      }
    } catch (err) {
      setAlert({ variant: "error", title: "System Error", message: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto p-8 bg-slate-900 border border-slate-800/80 rounded-2xl shadow-2xl shadow-black/40 mt-7 text-slate-100"
    >

      {alert && (
        <Alert
          variant={alert.variant} 
          title={alert.title} 
          message={alert.message} 
          autoCloseMs={5000}
          onClose={() => setAlert(null)} 
        />
      )}

      {/* Header Section */}
      <div className="mb-8 border-b border-slate-800/60 pb-5">
        <h2 className="text-2xl font-bold tracking-wide text-white flex items-center gap-2">
          Create New Post <span className="text-cyan-400 text-xs tracking-widest font-mono bg-cyan-950 px-2 py-0.5 rounded border border-cyan-800/30">TEMPLATE</span>
        </h2>
        <p className="text-slate-400 text-sm mt-1">Fill in the details to invite a hiring candidate to your Organization.</p>
      </div>
      

      <div className="space-y-6">
        {/* Row 1: Names */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextInput 
            label="Job Title" 
            name="jobTitle" 
            value={values.jobTittle} 
            onChange={handleInputChange("jobTittle")}
            placeholder="e.g. Senior QA Manager"
          />

          <DropdownSingle
            label="Job Type"
            value={values.jobSummarySlug}
            onChange={handleRequiredValueChange("jobSummarySlug")}
            options={[
              { label: "Full Time", value: "FULL_TIME" },
              { label: "Part Time", value: "PART_TIME" },
              { label: "Contract", value: "CONTRACT" },
            ]}
          />
        </div>

        <DropdownSingle
          label="What is your current professional level...?"
          options={roleLevel}
          value={values.roleLevel}
          onChange={(val) => handleValueChange("roleLevel")(val ?? "")} 
        />

        <DropdownMulti
          label="What level of ownership did you have in your last role...?"
          maxItems={3}
          options={ownerShipLevel}
          value={values.requiredOwnerShipLevel}
          onChange={handleRequiredValueChangeForDD("requiredOwnerShipLevel")}
        />


        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextInput 
            label="Minimum Experience Required (In Years)" 
            name="jobTitle" 
            value={values.miniExperience} 
            onChange={handleInputChange("miniExperience")}
            placeholder="e.g. 5 years"
          />

          <TextInput 
            label="Maximum Experience Required (In Years)" 
            name="jobTitle" 
            value={values.maxExperience} 
            onChange={handleInputChange("maxExperience")}
            placeholder="e.g. 10 years"
          />
        </div>

  

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextInput 
            label="Minimum Team Handled For Current Job Post" 
            name="requiredTeamSize" 
            value={values.requiredTeamSize} 
            onChange={handleInputChange("requiredTeamSize")}
            placeholder="e.g. 10 - 15"
          />

          <TextInput 
            label="Minimum Budget Handled For Current Job Post" 
            name="minBudgetHandled" 
            value={values.minBudgetHandled} 
            onChange={handleInputChange("minBudgetHandled")}
            placeholder="e.g. 500000"
            type="number"
          />
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextInput 
            label="Compensation Offer For Current Job Post ( 35 LPA )" 
            name="compensationOffer" 
            value={values.compensationOffer?.toString() || ""}
            onChange={handleInputChange("compensationOffer")}
            placeholder="e.g. 35 LPA"
          />
          <TextInput 
            label="Notice Period (In Days)" 
            name="noticePeriodDays" 
            value={values.noticePeriodDays?.toString() || ""} 
            onChange={handleInputChange("noticePeriodDays")}
            placeholder="e.g. 30"
            type="number"
          />
        </div>

        {/* <TextArea
          label="Job Description" 
          name="jobDescription" 
          value={values.jobDescription} 
          onChange={handleInputChange("jobDescription")}
          placeholder="e.g. We are looking for a Senior QA Manager..."
        /> */}

<RichTextArea
  label="Job Description" 
  name="jobDescription" 
  value={values.jobDescription} 
  onChange={handleInputChange("jobDescription")}
  placeholder="e.g. We are looking for a Senior QA Manager..."
/>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DropdownSingle
            label="What is your preferred work type...?"
            options={workTypeOptions}
            value={values.workType}
            onChange={(val) => handleValueChange("workType")(val ?? "")}
          />
        </div>

        <GenericListManager<string>
          candidate={values}
          setCandidate={setAllValues}
          dataKey="requiredDomain"
          title="Core Domains Expertise"
          maxItems={5}
          getLabel={(item) => item}
          getId={(item) => item}
          createItem={(input) => input}
        />

        <GenericListManager<string>
          candidate={values}
          setCandidate={setAllValues}
          dataKey="requiredTechSkills"
          title="Primary Skills"
          maxItems={7}
          getLabel={(item) => item}
          getId={(item) => item}
          createItem={(input) => input}
        />


        <GenericListManager<string>
          candidate={values}
          setCandidate={setAllValues}
          dataKey="expectedImpactLevel"
          title="Expected Impact Level"
          maxItems={5}
          getLabel={(item) => item}
          getId={(item) => item}
          createItem={(input) => input}
        />


        <GenericListManager<string>
          candidate={values}
          setCandidate={setAllValues}
          dataKey="location"
          title="Job Locations"
          maxItems={5}
          getLabel={(item) => item}
          getId={(item) => item}
          createItem={(input) => input}
        />

    

        {/* Action Section */}
        <div className="pt-6 mt-8 border-t border-slate-800/80 flex items-center justify-between">
          <div className="text-xs text-slate-500 font-mono tracking-tight">
            // ALL PARAMS MANDATORY FOR POST CREATION
          </div>
          <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
            <Button
              onClick={handleSubmit}
              className={`px-8 py-2.5 rounded-lg font-semibold tracking-wide transition-all duration-200 ${
                loading 
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700/50' 
                  : 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 shadow-lg shadow-cyan-500/10 active:scale-95'
              }`}
            >
              {loading ? "PROCESSING..." : "CREATE POST"}
            </Button>
          </motion.div>
        </div>
      </div>
      
    </motion.div>
  );
}